import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import "../components/auth-fields.css";

function AccessPIN() {
  const [pin, setPin] = useState(["", "", "", ""]);
  const [isValid, setIsValid] = useState(null); // null: initial, true: valid, false: invalid
  const [isComplete, setIsComplete] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  // For demo purposes - in real app, this would come from backend or user settings
  const correctPIN = "1234";

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    const pinString = pin.join("");
    setIsComplete(pinString.length === 4);

    if (pinString.length === 4) {
      const isCorrect = pinString === correctPIN;
      setIsValid(isCorrect);

      // Auto-verify when 4 digits are entered
      setTimeout(() => {
        if (isCorrect) {
          toast.success("PIN Verified Successfully!");
          // PIN verified successfully - redirect to main app
          // You can navigate to dashboard or main page here
          // navigate('/dashboard');
        } else {
          toast.error("Invalid PIN. Please try again.");
          // Auto-clear wrong PIN after 1 second
          setTimeout(() => {
            setPin(["", "", "", ""]);
            setIsValid(null);
            setIsComplete(false);
            inputRefs.current[0]?.focus();
          }, 200);
        }
      }, 300);
    } else {
      setIsValid(null);
    }
  }, [pin]);

  const handleInputChange = (index, value) => {
    // Only allow single digits
    if (value.length > 1) return;
    if (value !== "" && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    // Auto-focus next input
    if (value !== "" && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (pin[index] === "" && index > 0) {
        // Focus previous input if current is empty
        inputRefs.current[index - 1]?.focus();
      } else {
        // Clear current input
        const newPin = [...pin];
        newPin[index] = "";
        setPin(newPin);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 3) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    if (/^\d{4}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setPin(digits);
      inputRefs.current[3]?.focus();
    }
  };

  const handleClear = () => {
    setPin(["", "", "", ""]);
    setIsValid(null);
    setIsComplete(false);
    inputRefs.current[0]?.focus();
  };

  const handleVerify = () => {
    if (isComplete) {
      if (isValid) {
        toast.success("PIN Verified Successfully!");
      } else {
        toast.error("Invalid PIN. Please try again.");
      }
    }
  };

  const getInputClass = () => {
    return "form-control text-center fw-bold fs-4 access-pin-input";
  };

  const getStatusMessage = () => {
    if (isComplete) {
      if (isValid === true) {
        return (
          <div className="alert alert-success py-2 mb-0" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            PIN verified successfully!
          </div>
        );
      } else if (isValid === false) {
        return (
          <div className="alert alert-danger py-2 mb-0" role="alert">
            <i className="fas fa-times-circle me-2"></i>
            Invalid PIN. Please try again.
          </div>
        );
      }
    }
    return null;
  };

  return (
    <div className="auth-fields-scope container-fluid vh-100 d-flex align-items-center justify-content-center bg-white">
      <div style={{ maxWidth: "400px", width: "100%" }}>
        {/* Header */}
        <div className="text-center mb-4">
          <h3 className="fw-bold text-dark mb-2">Enter Access PIN</h3>
          <p className="text-muted small mb-0">
            Please enter your 4-digit access PIN
          </p>
        </div>

        {/* PIN Input Boxes */}
        <div className="row g-2 mb-4" onPaste={handlePaste}>
          {pin.map((digit, index) => (
            <div key={index} className="col-3">
              <input
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength="1"
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={getInputClass()}
                style={{
                  height: "60px",
                  fontSize: "24px",
                }}
              />
            </div>
          ))}
        </div>

        {/* Status Message */}
        <div className="mb-4" style={{ minHeight: "48px" }}>
          {getStatusMessage()}
        </div>
      </div>

      {/* Toast Container */}
      <Toaster
        position="top-center"
        reverseOrder={false}
        gutter={8}
        containerClassName=""
        containerStyle={{}}
        toastOptions={{
          className: "",
          duration: 4000,
          style: {
            background: "#fff",
            color: "#000",
          },
          success: {
            duration: 2000,
            theme: {
              primary: "green",
              secondary: "black",
            },
          },
          error: {
            duration: 2000,
            theme: {
              primary: "red",
              secondary: "black",
            },
          },
        }}
      />

      {/* Custom Styles */}
      <style jsx>{`
        .alert-danger {
          background-color: #ffffff;
          border-color: #b71c1c;
          color: #b71c1c;
        }

        .btn:hover {
          transform: translateY(-1px);
          transition: all 0.3s ease;
        }

        .btn:active {
          transform: translateY(0);
        }

        @media (max-width: 576px) {
          .form-control {
            height: 50px !important;
            font-size: 20px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default AccessPIN;
